import { UnixTime } from 'src/dataCache/time';
import { I_S3Credentials } from 'src/interfaces/S3Types';
import { FetchResult } from 'src/dataCache/dataCache';
import { S3Access } from 'src/dataCache/S3/S3Access';
import { JSONRecordData, parseChunkJson } from '../Log/graph.util';
import JSZip from 'jszip';

// Find the latest chunk file in S3 using ListObjects (single HTTP request)
// Returns the parsed chunk data or FetchResult.notAvailable
export const fetchLatestDataJson = (
  s3Credentials?: I_S3Credentials,
  maxAgeSeconds: number = 400
): Promise<FetchResult<Record<string, JSONRecordData>>> => {
  if (!s3Credentials || !s3Credentials.s3Bucket) {
    return Promise.resolve(FetchResult.notAvailable);
  }

  const s3Access = new S3Access(
    s3Credentials.s3Bucket,
    s3Credentials.s3Region,
    s3Credentials.s3Provider,
    s3Credentials.s3Key,
    s3Credentials.s3Secret
  );

  // Use marker to skip files older than maxAgeSeconds
  const oldestTimestamp = Math.floor(Date.now() / 1000) - maxAgeSeconds;
  const marker = `${oldestTimestamp}.json`;

  return s3Access
    .list(marker, 50)
    .then(async (r) => {
      if (r.status !== 200) {
        return Promise.resolve(FetchResult.notAvailable);
      }
      const xml = await r.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(xml, 'application/xml');
      const keys = Array.from(doc.getElementsByTagName('Key'))
        .map((el) => el.textContent)
        .filter((key) => key && /^\d+\.json$/.test(key))
        .sort((a, b) => Number(b.replace('.json', '')) - Number(a.replace('.json', '')));

      if (keys.length === 0) {
        return Promise.resolve(FetchResult.notAvailable);
      }

      // Fetch the most recent chunk file
      const latestKey = keys[0];
      const res = await s3Access.get(latestKey);
      if (res.status !== 200) {
        return Promise.resolve(FetchResult.notAvailable);
      }

      const jsontext = await res.text();
      const byteArray = Uint8Array.from(atob(jsontext), (c) =>
        c.charCodeAt(0)
      );
      const zip = await JSZip.loadAsync(byteArray);
      const jsonContent = await zip.file('data.json').async('text');
      return parseChunkJson(jsonContent);
    })
    .catch(() => {
      return Promise.resolve(FetchResult.tryLater);
    });
};

export const fetchDataJson = (
  timestamp: UnixTime,
  s3Credentials?: I_S3Credentials,
  cutdigits?: boolean
): Promise<FetchResult<Record<string, JSONRecordData>>> => {
  const s3Path = cutdigits
    ? `${timestamp.ticks.toString().slice(0, -2)}.json`
    : `${timestamp.ticks}.json`;
  if (s3Credentials && s3Credentials.s3Bucket) {
    const s3Access = new S3Access(
      s3Credentials.s3Bucket,
      s3Credentials.s3Region,
      s3Credentials.s3Provider,
      s3Credentials.s3Key,
      s3Credentials.s3Secret
    );

    return s3Access
      .get(s3Path)
      .then(async (r) => {
        if (r.status === 404) {
          return Promise.resolve(FetchResult.notAvailable);
        } else if (r.status === 200) {
          const jsontext = await r.text(); // Assuming the server returns the Base64 encoded ZIP file as text
          const byteArray = Uint8Array.from(atob(jsontext), (c) =>
            c.charCodeAt(0)
          );

          //Decompress the byte array using JSZip
          const zip = await JSZip.loadAsync(byteArray);
          // Assuming the Json file is named "data.json" inside the ZIP archive
          const jsonContent = await zip.file('data.json').async('text');
          return parseChunkJson(jsonContent);
        } else {
          return Promise.resolve(FetchResult.notAvailable);
        }
      })
      .catch((e) => {
        return Promise.resolve(FetchResult.tryLater);
      });
  }
};

export const fetchAggregatedDataJson = (
  date: string,
  s3Credentials?: I_S3Credentials,
  product?: number
): Promise<FetchResult<any>> => {
  const s3Path = `${date}.json`;

  if (s3Credentials && s3Credentials.s3Bucket) {
    const s3Access = new S3Access(
      s3Credentials.s3Bucket,
      s3Credentials.s3Region,
      s3Credentials.s3Provider,
      s3Credentials.s3Key,
      s3Credentials.s3Secret
    );
    return s3Access
      .get(s3Path)
      .then(async (r) => {
        if (r.status === 404) {
          return Promise.resolve(FetchResult.notAvailable);
        } else if (r.status === 200) {
          const jsontext = await r.text();

          if (product === 2 || product === 5) {
            return parseSinexcelAggregatedData(jsontext);
          }

          const contentEncoding = r.headers.get('content-type');

          if (contentEncoding != 'application/base64; charset=utf-8') {
            return JSON.parse(jsontext);
          }
          const byteArray = Uint8Array.from(atob(jsontext), (c) =>
            c.charCodeAt(0)
          );

          //Decompress the byte array using JSZip
          const zip = await JSZip.loadAsync(byteArray);
          // Assuming the CSV file is named "data.csv" inside the ZIP archive
          const jsonContent = await zip.file('data.json').async('text');
          return JSON.parse(jsonContent);
        } else {
          return Promise.resolve(FetchResult.notAvailable);
        }
      })
      .catch((e) => {
        return Promise.resolve(FetchResult.tryLater);
      });
  }
};

// The Sinexcel 12K TL applies a kW->W POWER factor (=1000) to two ENERGY registers, so PV arrives
// in Wh while every sibling value here is already kWh. The backend corrects the same field in
// AggregatedJsonParser.PvToKwh; this reads the raw S3 file directly and so has to do it too.
// Without it the topology view and the daily report show the same PV 1000x apart.
const PV_WH_PER_KWH = 1000;

const parseSinexcelAggregatedData = (jsontext: string): any => {
  const lines = jsontext.trim().split('\n');
  for (const line of lines) {
    const entry = JSON.parse(line);
    if (entry.Type === 'Daily') {
      return {
        PvPower: (entry.DailySelfGeneratedElectricity ?? 0) / PV_WH_PER_KWH,
        GridImportPower: entry.DailyElectricityPurchased ?? 0,
        GridExportPower: -(entry.DailyElectricityFed ?? 0),
        ChargingBatteryPower: entry.BatteryDailyChargeEnergy ?? 0,
        DischargingBatteryPower: -(entry.BatteryDailyDischargeEnergy ?? 0),
        LoadPowerConsumption: entry.DailyLoadPowerConsumption ?? 0
      };
    }
  }
  return FetchResult.notAvailable;
};

//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//                                                                       For CSV manipulation, check the following functions
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//--------------------------------------------------------------------------------------------------------------------------------------------------------------------
//
// export const fetchData = (
//   timestamp: UnixTime,
//   s3Credentials?: I_S3Credentials,
//   cutdigits?: boolean
// ): Promise<FetchResult<Record<string, DataRecord>>> => {
//   const s3Path = cutdigits
//     ? `${timestamp.ticks.toString().slice(0, -2)}.csv`
//     : `${timestamp.ticks}.csv`;
//   if (s3Credentials && s3Credentials.s3Bucket) {
//     const s3Access = new S3Access(
//       s3Credentials.s3Bucket,
//       s3Credentials.s3Region,
//       s3Credentials.s3Provider,
//       s3Credentials.s3Key,
//       s3Credentials.s3Secret
//     );
//
//     return s3Access
//       .get(s3Path)
//       .then(async (r) => {
//         if (r.status === 404) {
//           return Promise.resolve(FetchResult.notAvailable);
//         } else if (r.status === 200) {
//           //console.log('FOUND ITTTTTTTTTTTT');
//           const csvtext = await r.text(); // Assuming the server returns the Base64 encoded ZIP file as text
//           const contentEncoding = r.headers.get('content-type');
//
//           //console.log(contentEncoding);
//
//           if (contentEncoding != 'application/base64; charset=utf-8') {
//             // console.log('uncompressed');
//             return parseChunk(csvtext);
//           }
//
//           const byteArray = Uint8Array.from(atob(csvtext), (c) =>
//             c.charCodeAt(0)
//           );
//
//           //Decompress the byte array using JSZip
//           const zip = await JSZip.loadAsync(byteArray);
//           // Assuming the CSV file is named "data.csv" inside the ZIP archive
//           const csvContent = await zip.file('data.csv').async('text');
//
//           //console.log(csvContent);
//
//           return parseChunk(csvContent);
//         } else {
//           return Promise.resolve(FetchResult.notAvailable);
//         }
//       })
//       .catch((e) => {
//         return Promise.resolve(FetchResult.tryLater);
//       });
//   }
// };
//
// export const fetchAggregatedData = (
//   date: string,
//   s3Credentials?: I_S3Credentials
// ): Promise<FetchResult<DataRecord>> => {
//   const s3Path = `${date}.csv`;
//   if (s3Credentials && s3Credentials.s3Bucket) {
//     const s3Access = new S3Access(
//       s3Credentials.s3Bucket,
//       s3Credentials.s3Region,
//       s3Credentials.s3Provider,
//       s3Credentials.s3Key,
//       s3Credentials.s3Secret
//     );
//     return s3Access
//       .get(s3Path)
//       .then(async (r) => {
//         if (r.status === 404) {
//           return Promise.resolve(FetchResult.notAvailable);
//         } else if (r.status === 200) {
//           const csvtext = await r.text(); // Assuming the server returns the Base64 encoded ZIP file as text
//           const contentEncoding = r.headers.get('content-type');
//
//           if (contentEncoding != 'application/base64; charset=utf-8') {
//             return parseCsv(csvtext);
//           }
//
//           const byteArray = Uint8Array.from(atob(csvtext), (c) =>
//             c.charCodeAt(0)
//           );
//
//           //Decompress the byte array using JSZip
//           const zip = await JSZip.loadAsync(byteArray);
//           // Assuming the CSV file is named "data.csv" inside the ZIP archive
//           const csvContent = await zip.file('data.csv').async('text');
//           return parseCsv(csvContent);
//         } else {
//           return Promise.resolve(FetchResult.notAvailable);
//         }
//       })
//       .catch((e) => {
//         return Promise.resolve(FetchResult.tryLater);
//       });
//   }
// };
